import React, { FormEvent, useState } from 'react'
import { useIntl } from 'react-intl';
import { Link } from "react-router-dom";
import { db } from '../../db/db'
import { useForm } from '../../hooks/form-hook';
import { login, getUserData, getTutorials, loadPermissionByCompanyType, updateConfigUser } from '../../services/index'
import { VALIDATOR_EMAIL, VALIDATOR_PASSWORD } from '../../utils/validators';
import InputForm from '../../components/FormElements/InputForm';
import PrimaryButton from "../../components/FormElements/PrimaryButton";
import * as Styled from './styles';
import Logo from "../../assets/image/logo.svg";
import { toast } from 'react-toastify';
import { useLiveQuery } from 'dexie-react-hooks';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as Help } from '../../assets/image/Help.svg'
import Loading from '../../components/Loading';
import { privacyPolicyByLangugue } from '../../utils/privacyPolicy';
import { useDispatch } from 'react-redux';
import { setUser } from '../../store/user/userSlice';

const LoginPage = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userConfigDb = useLiveQuery(() => db.userConfigs.toArray());
  sessionStorage.removeItem('recoverAccount');
  const id: any = (userConfigDb ? (userConfigDb[0]?.id ?? 1) : 1);
  const [isLoading, setIsLoading] = useState(false);
  const [formState, inputHandler] = useForm(
    {
      email: {
        value: '',
        isValid: false,
      },
      password: {
        value: '',
        isValid: false,
      },
    },
    false
  );

  const submitLogin = async (event: FormEvent) => {
    event.preventDefault();
    setIsLoading(true);

    if (!formState.isValid) {
      setIsLoading(false);
      toast.error(intl.formatMessage({ id: 'menus.Existem campos com informações inválidas' }).toUpperCase());
      return;
    }
    const userInfo = {
      email: formState.inputs.email.value,
      password: formState.inputs.password.value
    };

    try {
      await login(userInfo)
        .then(async (resp) => {
          const { accessToken } = resp.data;
          const userDataResponse = await getUserData(accessToken);
          const { user, configs, company } = userDataResponse.data;
          const tutorialsDataResponse = await getTutorials(user.id ? user.id : 1, accessToken);
          const permissionDataResponse = await loadPermissionByCompanyType(
            company.companyType,
            Number(user.userTypeId),
            accessToken
          );
          const languageDb = userConfigDb && userConfigDb[0].language;

          const submit = {
            userConfigs: {
              id: configs.id,
              language: configs.language === languageDb ? configs.language : languageDb,
              tempUnit: configs.tempUnit,
              theme: configs.theme,
              userId: configs.userId,
              weightUnit: configs.weightUnit,
              tutorialsUnmade: tutorialsDataResponse.data,
              permission: permissionDataResponse.data,
            },
          };

          db.userConfigs.update(id, submit.userConfigs);

          if (configs.language !== languageDb) {
            const submit = {
              userConfigs: {
                id: configs.id,
                language: languageDb,
              },
            };
            updateConfigUser(submit, accessToken);
          }

          setTimeout(() => {
            db.user.add(user);
            db.company.add(company);
          }, 250);

          dispatch(
            setUser({
              user: user,
              userConfigs: configs,
            })
          );
        })
        .catch((error) => {
          setIsLoading(false);

          if (error.response) {
            const { status, data } = error.response;
            const remainingAttempts = data.error.remainingAttempts;

            if (status === 403 && error.message === 'Request failed with status code 403') {
              toast.error(intl.formatMessage({ id: 'login.Sua conta não foi ativada. Por gentileza, verifique seu e-mail para realizar a ativação' }).toUpperCase());
            } else if (status === 400 && remainingAttempts >= 0) {
              switch (remainingAttempts) {
                case 2:
                  toast.error(intl.formatMessage({ id: 'login.Senha incorreta! Duas tentativas restantes antes de ter o acesso à conta bloqueado.' }).toUpperCase());
                  break;
                case 1:
                  toast.error(intl.formatMessage({ id: 'login.Senha incorreta! Uma tentativa restantes antes de ter o acesso à conta bloqueado.' }).toUpperCase());
                  break;
                default:
                  toast.error(intl.formatMessage({ id: 'login.Acesso bloqueado! Siga as instruções de recuperação de conta.' }).toUpperCase());
                  sessionStorage.setItem('recoverAccount', 'true');
                  navigate('/recoverPassword', { state: { data: 'AccountAccessBlocked', email: formState.inputs.email.value } });
                  break;
              }
            } else {
              toast.error(intl.formatMessage({ id: 'login.Não foi possÍvel fazer o login. Verifique seus dados e tente novamente' }).toUpperCase());
            }
          } else {
            toast.error(intl.formatMessage({ id: 'login.Não foi possÍvel fazer o login. Verifique seus dados e tente novamente' }).toUpperCase());
          }
        });
    } catch (error) {
      setIsLoading(false);
      toast.error(intl.formatMessage({ id: 'login.Não foi possÍvel fazer o login. Verifique seus dados e tente novamente' }).toUpperCase());
    }
  };

  return (
    <>
      {isLoading && <Loading />}
      {!isLoading && (
        <>
          <Styled.DivLogo>
            <img src={Logo} alt='Logo' />
          </Styled.DivLogo>
          <Styled.Form onSubmit={submitLogin}>
            <Styled.DivInput>
              <InputForm
                id='email'
                type='email'
                label={intl.formatMessage({ id: 'login.Login' }) + ' *'}
                initialValue={formState.inputs.email.value}
                initialValid={!!formState.inputs.email.value}
                validators={[VALIDATOR_EMAIL()]}
                placeholder={intl.formatMessage({ id: 'login.Informe seu e-mail' })}
                errorText={intl.formatMessage({ id: 'login.E-mail inválido. Por favor, verifique' })}
                onInput={inputHandler}
                isLogin
              />
            </Styled.DivInput>
            <Styled.DivInput>
              <InputForm
                id='password'
                type='password'
                label={intl.formatMessage({ id: 'login.Senha' }) + ' *'}
                initialValue={formState.inputs.password.value}
                initialValid={!!formState.inputs.password.value}
                placeholder={intl.formatMessage({ id: 'main.Informe sua senha atual' })}
                onInput={inputHandler}
                isLogin
              />
            </Styled.DivInput>

            <Styled.DivInput>
              <Styled.DivQuestions>
                <Link to='/recoverPassword'>
                  {intl.formatMessage({ id: 'login.Esqueci minha senha' })}
                </Link>
              </Styled.DivQuestions>
            </Styled.DivInput>

            <Styled.DivButtons>
              <PrimaryButton type='submit' size='extra-large' model='white-solid' width='276px'>
                {intl.formatMessage({ id: 'login.ENTRAR' })}
              </PrimaryButton>
              <PrimaryButton type='button' size='extra-large' model='white-outline' width='276px' onClick={() => {
                const submit = {
                  userConfigs: {
                    id: userConfigDb && userConfigDb[0]?.id,
                    recover: false
                  }
                }
                db.userConfigs.update(id, submit.userConfigs);
                navigate('/entity')
              }}>
                {intl.formatMessage({ id: 'login.CRIAR CONTA' })}
              </PrimaryButton>
            </Styled.DivButtons>
            <Styled.Access >
              <Link to='/recoverAccount'>
                {intl.formatMessage({ id: 'login.Migrar Conta' })}
              </Link>
              <Styled.Img className="tooltip">
                <Help />
                <span className={`tooltiptext ${userConfigDb && userConfigDb[0]?.language === 'EL' ? 'greek' : ''}`}>{intl.formatMessage({ id: 'login.Se você já possui um cadastro na antiga plataforma, clique aqui em Migrar Conta para migrar seus dados e aproveitar toda a experiência IOK' })}</span>
              </Styled.Img>
            </Styled.Access>
            <Styled.Access >
              <Styled.Link href={privacyPolicyByLangugue(userConfigDb && userConfigDb[0]?.language ? userConfigDb[0]!.language : 'PT')} target="_blank" rel="noopener noreferrer">
                {intl.formatMessage({ id: 'login.Política de privacidade' })}
              </Styled.Link>
            </Styled.Access>

          </Styled.Form>
        </>
      )}
    </>
  )
}

export default LoginPage;
